import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, MapPin, Image as ImageIcon, ChevronLeft, ChevronRight, Map as MapIcon } from "lucide-react";
import { useRef } from "react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import SignboardsMap from "@/components/SignboardsMap";
import SafeImage from "@/components/SafeImage";
import { SIGNBOARDS, REGIONS, getSignboardsByRegion } from "@/data/signboards";

/**
 * Signboards hub — centralized landing page for NoJo Enterprise's outdoor
 * advertising story. Boards are grouped by region (Okinawa, Yokosuka,
 * future expansion), each rendered as a horizontally-scrolling slideshow
 * of preview tiles. Each tile links to its detail page.
 */
export default function SignboardsPage() {
  const { lang } = useOutletContext();
  const isJa = lang === "ja";

  const totalBoards = SIGNBOARDS.length;
  const totalRegions = new Set(SIGNBOARDS.map((s) => s.region)).size;
  const totalPhotos = SIGNBOARDS.reduce((n, s) => n + (s.photos?.length || 0), 0);

  return (
    <div data-testid="signboards-page" className="bg-white">
      <SEO {...PAGE_SEO.signboards[lang]} path={`/${lang}/signboards`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "看板掲出" : "Signboards", url: `/${lang}/signboards` },
        ])}
      />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#5B2E83] via-[#4A2570] to-[#3F1F5B] text-white">
        {/* Decorative tri-color stripe */}
        <div className="absolute inset-x-0 top-0 flex h-1">
          <span className="flex-1 bg-[#5B2E83]" />
          <span className="flex-1 bg-[#2F8F3C]" />
          <span className="flex-1 bg-[#0E5FB0]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-10 md:pt-14 pb-8 md:pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.32em] font-mono font-bold text-[#7AB8E8] uppercase mb-3">
                {isJa ? "看板掲出・屋外広告" : "Signboards · Outdoor Presence"}
              </div>
              <h1 className="text-3xl md:text-[2.75rem] lg:text-[3.25rem] font-display font-bold tracking-tight leading-[1.05]">
                {isJa ? (
                  <>
                    日本全国に広がる、<br />
                    <span className="text-[#7AB8E8]">JapanETCcardの看板。</span>
                  </>
                ) : (
                  <>
                    Our signboards <br className="hidden md:block" />
                    <span className="text-[#7AB8E8]">across Japan.</span>
                  </>
                )}
              </h1>
              <p className="mt-4 text-[14px] md:text-[15px] leading-[1.65] text-white/85 max-w-2xl">
                {isJa
                  ? "NoJo Enterpriseは、沖縄の国道58号線をはじめとした主要幹線沿いに、JapanETCcardの看板を展開しています。基地周辺、商業エリア、空港・港湾アクセス路線など、外国人ドライバーの目に届く場所に設置することで、日本でETCを必要とする方々とサービスをつなげています。"
                  : "NoJo Enterprise places JapanETCcard signboards along Japan's busiest arterial routes — starting with Route 58 in Okinawa. Positioned near U.S. military installations, commercial zones, airport corridors, and port access roads, our signs put the service directly in front of the foreign drivers who need it."}
              </p>

              {/* Stat strip */}
              <div className="mt-5 grid grid-cols-3 gap-2.5 max-w-lg">
                <Stat label={isJa ? "看板" : "Signboards"} value={String(totalBoards)} accent="#7AB8E8" />
                <Stat label={isJa ? "拠点エリア" : "Regions"} value={String(totalRegions)} accent="#7AB8E8" />
                <Stat label={isJa ? "ギャラリー写真" : "Gallery photos"} value={`${totalPhotos}+`} accent="#7AB8E8" />
              </div>
            </div>

            {/* Right-side: interactive Japan map showing every signboard.
                Clicking a pin opens a popup with the board title and a
                button that routes to that signboard's detail page. */}
            <div className="lg:col-span-5">
              <SignboardsMap lang={lang} isJa={isJa} variant="hero" />
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                <Link
                  to={`/${lang}/signboards/map/mainland`}
                  data-testid="signboards-hero-detailed-map-mainland"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-tight text-white/85 hover:text-white transition-colors group"
                >
                  <MapIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  {isJa ? "本州の詳細マップを開く" : "Open Mainland detailed map"}
                  <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </Link>
                <Link
                  to={`/${lang}/signboards/map/okinawa`}
                  data-testid="signboards-hero-detailed-map-okinawa"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-tight text-white/85 hover:text-white transition-colors group"
                >
                  <MapIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  {isJa ? "沖縄の詳細マップを開く" : "Open Okinawa detailed map"}
                  <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== REGION SECTIONS ===== */}
      {REGIONS.map((region) => {
        const boards = getSignboardsByRegion(region.key);
        if (boards.length === 0) return null;
        return (
          <RegionSection
            key={region.key}
            region={region}
            boards={boards}
            lang={lang}
            isJa={isJa}
          />
        );
      })}

      {/* ===== FUTURE EXPANSION BAND ===== */}
      <section className="relative py-10 md:py-14 bg-[#F8F9FA] border-t border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 text-center">
          <div className="text-[10.5px] tracking-[0.32em] font-mono font-bold text-[#5B2E83] uppercase mb-2">
            {isJa ? "今後の展開" : "Where we're heading next"}
          </div>
          <h2 className="text-2xl md:text-[2rem] font-display font-bold tracking-tight text-[#0F1D2D] leading-tight">
            {isJa
              ? "看板を、日本全国へ広げていきます。"
              : "Expanding signboard coverage across Japan."}
          </h2>
          <p className="mt-3 text-[13.5px] md:text-[14.5px] leading-[1.65] text-[#475569] max-w-3xl mx-auto">
            {isJa
              ? "本州、九州、北海道の主要都市と幹線道路への展開を計画中です。看板掲出のご相談、地域ごとの提携、共同キャンペーンについてはお問い合わせください。"
              : "We are planning expansion into Honshu, Kyushu, and Hokkaido — covering major cities and arterial highways. For partnership, joint campaigns, or signboard placement inquiries, get in touch."}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
            <Link
              to={`/${lang}/contact`}
              data-testid="signboards-cta-contact"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#663399] hover:bg-[#532880] text-white text-[13px] font-semibold tracking-tight transition-colors"
            >
              {isJa ? "看板掲出のお問い合わせ" : "Inquire about signboards"}
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link
              to={`/${lang}/services/japanetccard`}
              data-testid="signboards-cta-jec"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white border border-[#0F1D2D]/15 hover:border-[#5B2E83]/40 text-[#0F1D2D] text-[13px] font-semibold tracking-tight transition-colors"
            >
              {isJa ? "JapanETCcardについて" : "About JapanETCcard"}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 backdrop-blur px-3 py-3.5 text-center">
      <div className="text-2xl md:text-3xl font-display font-bold leading-none" style={{ color: accent }}>
        {value}
      </div>
      <div className="mt-1.5 text-[9.5px] tracking-[0.18em] uppercase font-bold text-white/70">
        {label}
      </div>
    </div>
  );
}

function RegionSection({ region, boards, lang, isJa }) {
  const scrollerRef = useRef(null);
  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section
      data-testid={`region-section-${region.key}`}
      className="py-8 md:py-12 border-b border-[#E2E8F0] last:border-b-0"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-end justify-between gap-4 mb-5 md:mb-7">
          <div className="min-w-0">
            <div
              className="text-[10.5px] tracking-[0.32em] font-mono font-bold uppercase mb-1.5"
              style={{ color: region.accent }}
            >
              {isJa ? "リージョン" : "Region"}
            </div>
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight text-[#0F1D2D] leading-[1.05]">
              {isJa ? region.ja : region.en}
            </h2>
            <p className="mt-2 text-[13px] md:text-[14px] leading-[1.6] text-[#475569] max-w-2xl">
              {region.description[lang]}
            </p>
          </div>
          {boards.length > 1 && (
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scroll(-1)}
                data-testid={`region-${region.key}-scroll-prev`}
                aria-label={isJa ? "前へ" : "Previous"}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#E2E8F0] text-[#0F1D2D] hover:border-[#5B2E83] hover:text-[#5B2E83] transition-colors"
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                data-testid={`region-${region.key}-scroll-next`}
                aria-label={isJa ? "次へ" : "Next"}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#E2E8F0] text-[#0F1D2D] hover:border-[#5B2E83] hover:text-[#5B2E83] transition-colors"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={scrollerRef}
          data-testid={`region-${region.key}-scroller`}
          className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:thin]"
        >
          {boards.map((board) => (
            <SignboardCard key={board.slug} board={board} lang={lang} isJa={isJa} accent={region.accent} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SignboardCard({ board, lang, isJa, accent }) {
  const hero = board.photos?.[0] || null;
  const photoCount = board.photos?.length || 0;
  return (
    <Link
      to={`/${lang}/signboards/${board.slug}`}
      data-testid={`signboard-card-${board.slug}`}
      className="group snap-start shrink-0 w-[88%] sm:w-[420px] md:w-[460px] rounded-3xl overflow-hidden ring-1 ring-[#E2E8F0] bg-white hover:ring-[#0F1D2D]/30 hover:shadow-[0_24px_60px_-24px_rgba(15,29,45,0.25)] transition-all"
    >
      <div className="relative aspect-[4/3] bg-[#F1F5F9] overflow-hidden">
        {hero ? (
          <SafeImage
            src={hero}
            alt={board.title[lang]}
            imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
            fallbackLabel={isJa ? "写真準備中" : "Photos coming soon"}
            fallbackIconClassName="h-8 w-8"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
            <ImageIcon className="h-8 w-8 mb-2" strokeWidth={1.4} />
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold">
              {isJa ? "写真準備中" : "Photos coming soon"}
            </span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span
            className="text-[10px] tracking-[0.18em] uppercase font-bold text-white px-2.5 py-1 rounded-full"
            style={{ backgroundColor: accent }}
          >
            {board.route || (isJa ? "本社拠点" : "HQ")}
          </span>
          {photoCount > 0 && (
            <span className="text-[10px] font-semibold text-[#0F1D2D] bg-white/95 backdrop-blur px-2.5 py-1 rounded-full">
              {photoCount} {isJa ? "枚" : "photos"}
            </span>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[12px] text-[#64748B]">
          <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="truncate">{board.location[lang]}</span>
        </div>
        <h3 className="mt-2 text-[18px] md:text-[20px] font-display font-bold tracking-tight text-[#0F1D2D] leading-tight">
          {board.title[lang]}
        </h3>
        <p className="mt-2 text-[13px] leading-[1.6] text-[#475569] line-clamp-2">
          {board.summary[lang]}
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold tracking-tight" style={{ color: accent }}>
          {isJa ? "詳細を見る" : "View detail"}
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
        </div>
      </div>
    </Link>
  );
}
